import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type { AiHugQualityId, AiHugTemplateId } from "./ai-hug-preset";

/**
 * /ai-hug-video-generator 样片区的片子；第一条同时是工作台右栏唯一的 Example。
 *
 * 自产片在本页工作台用预设出，onSite true，标档位，「Try this example」回填原图并切到出片的拥抱方式。
 * 来源和压制参数记在 packages/scripts/ai-hug-showcase/README.md。
 *
 * R2 `home/showcase/ai-hug/` 下 clips/{id}-720p.mp4、posters/{id}-720p.jpg、refs/{ref}.jpg；
 * 右栏和样片区用同一份 720p 文件。清单为空时样片区整个不渲染。
 */
export interface AiHugShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档（只有自产片有），拼贴 hover 时显示档位名 */
	quality?: AiHugQualityId;
	/** 出这条片用的拥抱方式；「Try this example」顺带切过去 */
	template?: AiHugTemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 原图（合照一张，或两张单人照），「Try this example」填进工作台的槽 */
	inputImages?: string[];
}

const R2_BASE = "home/showcase/ai-hug";

/**
 * 视频和 poster 地址的版本参数（assets 域边缘缓存一天，连 404 也缓存）；换片子时加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<AiHugShowcaseClip, "videoUrl" | "poster" | "inputImages"> & {
	/** 自产片的原图（refs/ 下的文件名，或别的页面 refs 的完整 R2 路径） */
	refs?: string[];
};

/**
 * 顺序即拼贴顺序，第一条是右栏 Example。全是本页工作台自产片（老公 10/29 定方案 A：全竖版、不用竞品横版片），
 * 2026-10-29 本页 HD 出（Seedance 2.0 Mini 720p 5 秒 9:16，41 积分）
 */
const SPECS: Spec[] = [
	{
		id: "reunion-mom-son",
		title: "Mother and Son Reunion Hug from Two Selfies",
		description:
			"Two separate selfies of a mother and her grown son become one reunion: they spot each other on a sunny park path, hurry in and share a tight, joyful hug.",
		quality: "hd",
		template: "reunion",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["mom-single", "son-single"],
	},
	{
		id: "warm-friends",
		title: "Best Friends Warm Hug from One Photo",
		description:
			"One photo of two best friends becomes a warm hug by a sunlit window: they smile at each other, step in and hold the embrace with a gentle sway.",
		quality: "hd",
		template: "warm",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["friends-duo"],
	},
	// 11/01 补：右栏样片跟 tab 走，Back hug 原来没有自己的片
	{
		id: "back-couple",
		title: "Couple Back Hug from One Photo",
		description:
			"One couple photo becomes a back hug by a sunny window: he steps in behind her, wraps his arms around her waist and they both break into a smile.",
		quality: "hd",
		template: "back",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["home/showcase/ai-wedding/refs/cafe-couple.jpg"],
	},
];

const refUrl = (ref: string) =>
	buildR2AssetsImageUrl(
		ref.startsWith("home/") ? ref : `${R2_BASE}/refs/${ref}.jpg`,
	);

export const aiHugShowcaseClips: AiHugShowcaseClip[] = SPECS.map(
	({ refs, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}-720p.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: refs?.map(refUrl),
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const AI_HUG_SHOWCASE_UPLOADED_AT = "2026-10-29T00:00:00+08:00";
